import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Mail, Calendar, Clock, FileText,
  Phone, Plus, Edit, Activity,
  Linkedin, ExternalLink, Trash2,
  Briefcase, IndianRupee, Timer, MessageSquare, History,
  Layers, MapPin, ArrowLeft, Route, UserCircle, LayoutGrid, UserPlus, ChevronDown,
  AlertTriangle, Info, Video
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { candidateService } from '@/services/candidate.service';
import { interviewService } from '@/services/interview.service';
import { Candidate, Interview, HiringStatus } from '@/types/models';
import LoadingSpinner from '@/components/LoadingSpinner';
import { baseURL } from '@/lib/axios';
import { useSharedData } from '@/hooks/useSharedData';
import { AddInterviewDialog } from '@/components/hiring/AddInterviewDialog';
import { AddCandidateDialog } from '@/components/hiring/AddCandidateDialog';

const normalizeSalary = (value: any): string | undefined => {
  if (!value && value !== 0) return undefined;
  // If it's a number, convert to string. If it's already a string, keep it.
  return String(value);
};

const getCompleteBackendUrl = (resumeLink: string) => {
  if (!resumeLink) return '';
  const fileName = resumeLink.split('/').pop() || '';
  const serverRoot = baseURL.split('/api/')[0];
  return `${serverRoot}/uploads/${fileName}`;
};

export default function CandidateProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { departments } = useSharedData();

  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingInterviews, setIsLoadingInterviews] = useState(false);

  // Dialog states
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddInterviewDialogOpen, setIsAddInterviewDialogOpen] = useState(false);
  const [editingInterview, setEditingInterview] = useState<Interview | null>(null);
  const [expandedStatusGroup, setExpandedStatusGroup] = useState<string | null>(null);

  // Feedback states
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  // Confirmation states
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    cancelText?: string;
    type?: 'danger' | 'warning' | 'info';
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => { }
  });

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const candData = await candidateService.getCandidateById(id);
      setCandidate(candData);

      setIsLoadingInterviews(true);
      const interviewResponse = await interviewService.getInterviews({ candidate: id });
      setInterviews(interviewResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch candidate data:', error);
      toast.error('Failed to load candidate profile');
      navigate('/hiring/candidates');
    } finally {
      setIsLoading(false);
      setIsLoadingInterviews(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateStatusConfirmed = async (status: string, notes?: string) => {
    if (!candidate) return;
    try {
      const updatedCand = await candidateService.updateCandidateStatus(candidate._id, status, notes);
      setCandidate(updatedCand);
      toast.success(`Status updated to ${candidateService.getStatusDisplayText(status)}`);
      setIsFeedbackDialogOpen(false);
      setFeedbackText('');
      setPendingStatus(null);

      // Auto-open interview dialog if status is INTERVIEW_SCHEDULED
      if (status === HiringStatus.INTERVIEW_SCHEDULED) {
        setIsAddInterviewDialogOpen(true);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const showConfirmDialog = (
    title: string,
    message: string,
    onConfirm: () => void,
    options?: {
      confirmText?: string;
      cancelText?: string;
      type?: 'danger' | 'warning' | 'info';
    }
  ) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
      confirmText: options?.confirmText || 'Confirm',
      cancelText: options?.cancelText || 'Cancel',
      type: options?.type || 'info'
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  const handleConfirmAction = () => {
    confirmDialog.onConfirm();
    closeConfirmDialog();
  };

  const handleUpdateStatus = async (status: string) => {
    if (!candidate) return;

    const statusStr = String(status).toUpperCase();

    // Reset feedback state first to be safe
    if (statusStr === HiringStatus.INTERVIEW_COMPLETED) {
      setFeedbackText('');
      setPendingStatus(statusStr);
      setIsFeedbackDialogOpen(true);
      return;
    }

    if (isRejectedStatus(statusStr)) {
      showConfirmDialog(
        'Terminate Application',
        `Are you sure you want to terminate the application for ${candidateService.getCandidateFullName(candidate)}?`,
        () => updateStatusConfirmed(statusStr),
        {
          confirmText: 'Terminate',
          cancelText: 'Cancel',
          type: 'danger'
        }
      );
    } else {
      await updateStatusConfirmed(statusStr);
    }
  };

  const handleDownloadResume = async () => {
    if (!candidate?.resumeLink) {
      toast.error("No resume available");
      return;
    }
    try {
      const url = getCompleteBackendUrl(candidate.resumeLink);
      window.open(url, '_blank');
      toast.info("Resume Opened", {
        description: "Resume opened in new tab.",
      });
    } catch (error) {
      console.error('Error opening resume:', error);
      toast.error("Failed to open resume");
    }
  };

  const getCandidateInitials = (cand: Candidate) => {
    if (!cand.firstName) return '?';
    return `${cand.firstName[0]}${cand.lastName ? cand.lastName[0] : ''}`.toUpperCase();
  };

  const formatTimeStr = (str: string) => {
    if (!str) return '';
    // Check if it's ISO format
    if (str.includes('T') || str.includes('Z')) {
      const d = new Date(str);
      if (!isNaN(d.getTime())) {
        return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
      }
    }
    // Check if it's HH:mm:ss or HH:mm
    const timeMatch = str.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (timeMatch) {
      let [_, hours, minutes] = timeMatch;
      let h = parseInt(hours);
      const m = minutes;
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12;
      h = h ? h : 12; // the hour '0' should be '12'
      return `${h}:${m} ${ampm}`;
    }
    return str;
  };

  const isRejectedStatus = (status: any) => {
    const statusStr = String(status).toUpperCase();
    return statusStr === HiringStatus.REJECTED ||
      statusStr === HiringStatus.CANDIDATE_REJECTED ||
      statusStr === HiringStatus.OFFER_REJECTED;
  };




  const getNextActions = (candidateObj: Candidate) => {
    const status = String(candidateObj.status).toUpperCase() as HiringStatus;
    const actions: { label: string, status: HiringStatus }[] = [];

    switch (status) {
      case HiringStatus.NEW:
        actions.push({ label: 'Select for Interview', status: HiringStatus.SELECTED_FOR_INTERVIEW });
        actions.push({ label: 'Reject', status: HiringStatus.REJECTED });
        break;
      case HiringStatus.SELECTED_FOR_INTERVIEW:
        actions.push({ label: 'Shortlist', status: HiringStatus.SHORTLISTED });
        actions.push({ label: 'Schedule Interview', status: HiringStatus.INTERVIEW_SCHEDULED });
        actions.push({ label: 'Reject', status: HiringStatus.REJECTED });
        break;
      case HiringStatus.SHORTLISTED:
        actions.push({ label: 'Schedule Interview', status: HiringStatus.INTERVIEW_SCHEDULED });
        actions.push({ label: 'Reject', status: HiringStatus.REJECTED });
        break;
      case HiringStatus.INTERVIEW_SCHEDULED:
        actions.push({ label: 'Complete Interview', status: HiringStatus.INTERVIEW_COMPLETED });
        actions.push({ label: 'Reject', status: HiringStatus.REJECTED });
        break;
      case HiringStatus.INTERVIEW_COMPLETED:
        actions.push({ label: 'Candidate Selected', status: HiringStatus.CANDIDATE_SELECTED });
        actions.push({ label: 'Candidate Rejected', status: HiringStatus.CANDIDATE_REJECTED });
        break;
      case HiringStatus.CANDIDATE_SELECTED:
        actions.push({ label: 'Send Offer', status: HiringStatus.OFFER_SENT });
        break;
      case HiringStatus.OFFER_SENT:
        actions.push({ label: 'Offer Accepted', status: HiringStatus.OFFER_ACCEPTED });
        actions.push({ label: 'Offer Rejected', status: HiringStatus.OFFER_REJECTED });
        break;
      case HiringStatus.OFFER_ACCEPTED:
        actions.push({ label: 'Hire', status: HiringStatus.HIRED });
        break;
      default:
        break;
    }
    return actions;
  };

  const getInterviewerName = (item: any, format: 'full' | 'short' = 'full') => {
    if (!item) return 'Unknown';
    if (typeof item === 'object') {
      const firstName = item.user?.firstName || item.firstName || '';
      const lastName = item.user?.lastName || item.lastName || '';

      if (!firstName && !lastName) {
        if (item.name) return item.name;
        return 'Unknown';
      }

      if (format === 'short') {
        return `${firstName}${lastName ? ' ' + lastName.charAt(0) + '.' : ''}`.trim();
      }
      return `${firstName} ${lastName}`.trim();
    }
    return String(item);
  };

  const getDepartmentName = (deptId: any) => {
    if (!deptId) return '—';
    if (typeof deptId === 'object' && deptId.name) return deptId.name;
    const dept = departments.find(d => d._id === deptId);
    return dept ? dept.name : '—';
  };

  if (isLoading) {
    return <LoadingSpinner size="lg" text="Loading Candidate Profile..." />;
  }

  if (!candidate) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Activity className="h-12 w-12 text-gray-300" />
        <h2 className="text-xl font-semibold text-gray-600">Candidate not found</h2>
        <Button onClick={() => navigate('/hiring/candidates')}>Back to Candidates</Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4 pt-0 pb-10s">
      <div className="flex items-center justify-between mb-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/hiring/candidates')}
          className="text-gray-500 hover:text-indigo-600 h-8 text-[13px] font-semibold px-2 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Candidates
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsEditDialogOpen(true)}
          className="text-indigo-600 border-indigo-200 bg-indigo-50/50 hover:bg-indigo-100/80 hover:text-indigo-700 hover:border-indigo-300 h-8 text-[13px] font-bold px-4 rounded-xl transition-all flex items-center gap-2 shadow-sm"
        >
          <Edit className="h-4 w-4" /> Edit Profile
        </Button>
      </div>

      <div className="space-y-4">
        {/* Profile Header */}
        <div className="bg-indigo-200/50 rounded-2xl border border-indigo-300/60 p-5 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 bg-white border border-gray-200 rounded-xl flex items-center justify-center text-indigo-600 text-lg font-bold shrink-0 shadow-sm">
                {getCandidateInitials(candidate)}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h4 className="text-2xl font-bold uppercase text-gray-900 tracking-tight">
                    {candidateService.getCandidateFullName(candidate)}
                  </h4>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="h-fit w-fit bg-transparent border-none p-0 focus:outline-none hover:opacity-80 transition-opacity">
                        <Badge
                          variant="outline"
                          className="text-[11px] font-bold px-2.5 py-0.5 rounded-md border flex items-center gap-1 cursor-pointer transition-colors"
                          style={{
                            backgroundColor: 'transparent',
                            color: candidateService.getStatusColor(String(candidate.status)),
                            borderColor: candidateService.getStatusColor(String(candidate.status)) + '40',
                          }}
                        >
                          {candidateService.getStatusDisplayText(candidate.status)}
                          <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
                        </Badge>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="rounded-xl border-gray-200 shadow-xl min-w-[200px] p-1 overflow-y-auto max-h-[400px]">
                      {[
                        { label: 'Screening', statuses: [HiringStatus.SELECTED_FOR_INTERVIEW, HiringStatus.REJECTED] },
                        { label: 'Interviews', statuses: [HiringStatus.INTERVIEW_SCHEDULED, HiringStatus.INTERVIEW_COMPLETED, HiringStatus.SHORTLISTED] },
                        { label: 'Selection', statuses: [HiringStatus.CANDIDATE_SELECTED, HiringStatus.CANDIDATE_REJECTED] },
                        { label: 'Pipeline Management', statuses: [HiringStatus.FUTURE_REFERENCE, HiringStatus.ON_HOLD] },
                        { label: 'Offer & Hiring', statuses: [HiringStatus.OFFER_SENT, HiringStatus.OFFER_ACCEPTED, HiringStatus.OFFER_REJECTED, HiringStatus.HIRED] }
                      ].map((group) => (
                        <div key={group.label} className="mb-1 last:mb-0">
                          <button
                            className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold text-indigo-400 uppercase hover:bg-indigo-50/50 rounded-md transition-colors"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setExpandedStatusGroup(expandedStatusGroup === group.label ? null : group.label);
                            }}
                          >
                            {group.label}
                            <ChevronDown className={`h-3 w-3 transition-transform ${expandedStatusGroup === group.label ? 'rotate-180' : ''}`} />
                          </button>
                          {expandedStatusGroup === group.label && (
                            <div className="mt-0.5 space-y-0.5 pl-1">
                              {group.statuses.map((status) => {
                                if (status === 'NEW') return null;
                                const isCurrentStatus = String(candidate.status) === status;
                                return (
                                  <DropdownMenuItem
                                    key={status}
                                    className={`text-[11px] py-2 px-3 rounded cursor-pointer ${isCurrentStatus ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-700 focus:bg-gray-50'}`}
                                    disabled={isCurrentStatus}
                                    onClick={() => handleUpdateStatus(status)}
                                  >
                                    {candidateService.getStatusDisplayText(status)}
                                  </DropdownMenuItem>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-500 text-xs">
                  <p className="font-semibold text-indigo-600 flex items-center gap-1">
                    <Briefcase className="h-3.5 w-3.5" />
                    {candidate.position
                      ? typeof candidate.position === 'object'
                        ? (candidate.position as any).title || (candidate.position as any).name || (candidate.position as any).role || 'General'
                        : candidate.position
                      : 'General'}
                  </p>
                  <span className="text-gray-300 hidden sm:inline">•</span>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-gray-600">Applied on {new Date(candidate.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0 pt-3 md:pt-0 border-t md:border-t-0 font-sans">
              <div className="flex items-center gap-2">
                {getNextActions(candidate).map((action, index) => {
                  const isReject = isRejectedStatus(action.status);
                  return (
                    <Button
                      key={index}
                      variant="outline"
                      onClick={() => handleUpdateStatus(action.status)}
                      className={`h-9 px-4 font-bold text-[11px] uppercase rounded-lg border-2 transition-all ${isReject
                        ? 'bg-red-500/10 text-red-600 border-red-200/50 hover:bg-red-500/20 hover:border-red-300 hover:text-red-700'
                        : 'bg-indigo-500/10 text-indigo-600 border-indigo-200/50 hover:bg-indigo-500/20 hover:border-indigo-300 hover:text-indigo-700'}`}
                    >
                      {action.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {candidate.feedback && (
              <div className="bg-amber-50/50 rounded-xl border border-amber-100 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 bg-amber-100 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-4 w-4 text-amber-600" />
                  </div>
                  <h3 className="text-[11px] font-bold text-amber-900 uppercase tracking-widest">Interviewer Feedback</h3>
                </div>
                <p className="text-[13px] text-gray-700 border-l-4 border-amber-200 pl-4 py-1 leading-relaxed break-words break-all whitespace-pre-wrap">{candidate.feedback}</p>
              </div>
            )}

            {(interviews.length > 0 || String(candidate.status) === HiringStatus.INTERVIEW_SCHEDULED || String(candidate.status) === HiringStatus.INTERVIEW_COMPLETED) && (
              <div className="bg-blue-200/50 rounded-2xl border border-blue-300/60 overflow-hidden shadow-sm">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/30">
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2.5 uppercase tracking-widest">
                    <div className="w-8 h-8 bg-blue-100/50 rounded-lg flex items-center justify-center">
                      <Layers className="h-4 w-4 text-blue-600" />
                    </div>
                    Interview Rounds
                    <Badge variant="secondary" className="ml-1 text-[11px] font-bold bg-blue-100 text-blue-700 border-blue-200">
                      {interviews.length}
                    </Badge>
                  </h3>
                  <Button onClick={() => setIsAddInterviewDialogOpen(true)} variant="outline" size="sm" className="h-9 text-xs font-bold px-4 rounded-xl border-gray-200 bg-white hover:bg-gray-50 flex items-center gap-2">
                    <Plus className="h-4 w-4" /> Add Round
                  </Button>
                </div>
                <div className="p-3 space-y-2">
                  {isLoadingInterviews ? (
                    <LoadingSpinner 
                      fullScreen={false} 
                      size="md" 
                      text="Loading Interviews..." 
                      containerClassName="py-12"
                    />
                  ) : interviews.length > 0 ? (
                    interviews.map((interview, idx) => (
                      <div key={interview._id} className="group flex flex-col p-3 bg-white rounded-xl border border-gray-100 transition-all shadow-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-8 h-8 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center shrink-0 text-sm font-black text-gray-400 transition-colors">
                              {idx + 1}
                            </div>
                            <div>
                              <h4 className="text-[13px] font-bold text-gray-800 tracking-tight">{interview.interviewType}</h4>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1">
                                <span className="flex items-center gap-1.5 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-100">
                                  <Calendar className="h-3 w-3 text-gray-400" />
                                  {interview.interviewDate ? new Date(interview.interviewDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'Date TBD'}
                                </span>
                                {interview.interviewTime && (
                                  <span className="flex items-center gap-1.5 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-100">
                                    <Clock className="h-3 w-3 text-gray-400" />
                                    <span className="text-[11px] font-medium">{formatTimeStr(interview.interviewTime)}</span>
                                  </span>
                                )}
                                {interview.location && (
                                  <span className="flex items-center">
                                    {(interview.location.includes('http') || interview.location.includes('meet') || interview.location.includes('zoom') || interview.location.includes('teams')) ? (
                                      <a
                                        href={interview.location.startsWith('http') ? interview.location : `https://${interview.location}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1.5 bg-blue-50/50 hover:bg-blue-100 px-2 py-0.5 rounded-md border border-blue-200/60 text-blue-600 transition-colors group cursor-pointer"
                                      >
                                        <Video className="h-3 w-3 group-hover:scale-110 transition-transform" />
                                        <span className="text-[11px] font-bold">Meeting Link</span>
                                        <ExternalLink className="h-2.5 w-2.5 opacity-60" />
                                      </a>
                                    ) : (
                                      <span className="flex items-center gap-1.5 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-100">
                                        <MapPin className="h-3 w-3 text-gray-400" />
                                        <span className="text-[11px] font-medium text-gray-600 max-w-[120px] truncate" title={interview.location}>{interview.location}</span>
                                      </span>
                                    )}
                                  </span>
                                )}
                                {interview.interviewers?.[0] && (
                                  <TooltipProvider delayDuration={0}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="flex items-center gap-1.5 text-indigo-600 font-semibold bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100 cursor-help transition-colors hover:bg-indigo-100/80">
                                          <UserCircle className="h-3 w-3" />
                                          {getInterviewerName(interview.interviewers[0], 'short')}
                                          {interview.interviewers.length > 1 && (
                                            <span className="text-[10px] bg-indigo-200/50 text-indigo-700 px-1 py-0.5 rounded ml-0.5 font-bold border border-indigo-200">
                                              +{interview.interviewers.length - 1}
                                            </span>
                                          )}
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent align="start" sideOffset={5} className="bg-white p-3 rounded-xl border border-gray-100 shadow-xl min-w-[180px] z-50">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2.5 pb-2 border-b border-gray-50 flex items-center gap-1.5">
                                          <Layers className="h-3 w-3" /> Interview Panel
                                        </p>
                                        <div className="flex flex-col gap-2.5">
                                          {interview.interviewers.map((interviewer, i) => (
                                            <div key={i} className="flex items-center gap-2.5 group/interviewer">
                                              <div className="w-6 h-6 bg-indigo-50/50 group-hover/interviewer:bg-indigo-100 rounded-full flex items-center justify-center border border-indigo-100 transition-colors shrink-0">
                                                <UserCircle className="h-3.5 w-3.5 text-indigo-500" />
                                              </div>
                                              <span className="text-[12.5px] font-bold text-gray-700 group-hover/interviewer:text-indigo-700 transition-colors">
                                                {getInterviewerName(interviewer, 'full')}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                            <Button variant="ghost" size="sm" onClick={() => { setEditingInterview(interview); setIsAddInterviewDialogOpen(true); }} className="h-8 w-8 p-0 text-gray-400 hover:text-indigo-600 hover:bg-white border hover:border-indigo-100 shadow-sm rounded-lg"><Edit className="h-4 w-4" /></Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                showConfirmDialog(
                                  'Remove Round',
                                  `Are you sure you want to remove Round ${idx + 1}?`,
                                  async () => {
                                    try {
                                      await interviewService.deleteInterview(interview._id!);
                                      setInterviews(prev => prev.filter(i => i._id !== interview._id));
                                      toast.success('Removed');
                                    } catch (err) {
                                      console.error(err);
                                    }
                                  },
                                  { type: 'danger', confirmText: 'Remove' }
                                );
                              }}
                              className="h-8 w-8 p-0 text-gray-400 hover:text-red-500 hover:bg-white border hover:border-red-100 shadow-sm rounded-lg"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Interview Notes Section */}
                        {interview.notes && (
                          <div className="mt-2.5 pl-12 pr-4 pb-1">
                            <div className="border-l-2 border-amber-300/60 pl-3 py-1 bg-gradient-to-r from-amber-50/30 to-transparent rounded-r-lg">
                              <p className="text-[10px] font-black text-amber-800/50 uppercase tracking-[0.15em] mb-1 flex items-center gap-1.5">
                                <FileText className="h-3 w-3" /> Interview Notes
                              </p>
                              <p className="text-[12px] text-gray-600 font-medium whitespace-pre-wrap break-words leading-relaxed">
                                {interview.notes}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-10 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                      <div className="inline-flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full mb-3">
                        <Clock className="h-5 w-5 text-gray-400" />
                      </div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">No interview rounds scheduled yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div>

            </div>
            <div className="bg-indigo-50/50 rounded-2xl border border-indigo-100 overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-indigo-100/50 bg-indigo-50/30">
                <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2.5 uppercase tracking-widest">
                  <div className="w-8 h-8 bg-indigo-100/60 rounded-lg flex items-center justify-center border border-indigo-200">
                    <Briefcase className="h-4 w-4 text-indigo-700" />
                  </div>
                  Candidate Summary
                </h3>
              </div>
              <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { label: 'Experience', value: `${candidate.experienceYears || 0} Years`, icon: History },
                  { label: 'Current CTC', value: normalizeSalary(candidate.currentSalary) || '—', icon: IndianRupee },
                  { label: 'Expected CTC', value: normalizeSalary(candidate.expectedSalary) || '—', icon: IndianRupee },
                  { label: 'Notice Period', value: candidate.noticePeriod || '—', icon: Timer },
                  { label: 'Department', value: getDepartmentName(candidate.department), icon: LayoutGrid },
                  { label: 'Source', value: typeof candidate.source === 'object' ? (candidate.source as any).name : candidate.source || 'Direct', icon: Route },
                  { label: 'Communication', value: candidate.communicationSkills || '—', icon: MessageSquare },
                  { label: 'Current Role', value: candidate.currentRole || '—', truncate: true, icon: UserPlus }
                ].map((item, i) => (
                  <div key={i} className="min-w-0 bg-white/90 p-4 rounded-xl border border-white/60 shadow-sm transition-transform hover:-translate-y-0.5 duration-200">
                    <div className="flex items-center gap-2 mb-2">
                      <item.icon className="h-4 w-4 text-indigo-500/70" />
                      <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">{item.label}</p>
                    </div>
                    <p className={`text-[13px] font-semibold text-gray-800 ${item.truncate ? 'truncate' : ''}`} title={item.truncate ? String(item.value) : ''}>{item.value}</p>
                  </div>
                ))}
              </div>
              {candidate.reasonForLeavingPreviousRole && (
                <div className="px-4 pb-4">
                  <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="h-4 w-4 text-gray-400" />
                      <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Reason for Job Change</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 leading-relaxed">{candidate.reasonForLeavingPreviousRole}</p>
                  </div>
                </div>
              )}
            </div>

          </div>

          <div className="space-y-4">
            {/* ── Contact & Info Card ── */}
            <div className="bg-emerald-50/50 rounded-2xl border border-emerald-200/60 overflow-hidden shadow-sm">
              <div className="bg-gradient-to-r from-emerald-50 via-teal-50/60 to-cyan-50/40 px-5 py-3.5 border-b border-emerald-100/60">
                <h3 className="text-[11px] font-extrabold text-emerald-800 uppercase tracking-[0.15em] flex items-center gap-2">
                  <div className="w-6 h-6 bg-white/80 rounded-md flex items-center justify-center border border-emerald-200/60 shadow-sm">
                    <UserCircle className="h-3.5 w-3.5 text-emerald-600" />
                  </div>
                  Contact Details
                </h3>
              </div>
              <div className="p-3 space-y-1">
                {/* Email */}
                <div
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 hover:bg-white/60 group"
                  onClick={() => { if (candidate.email) { navigator.clipboard.writeText(candidate.email); toast.success('Email copied'); } }}
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-50 to-blue-100/80 rounded-lg flex items-center justify-center shrink-0 border border-blue-200/50 shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all duration-200">
                    <Mail className="h-3.5 w-3.5 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.12em] mb-0.5">Email</p>
                    <p className="text-[13px] font-semibold text-gray-800 truncate">{candidate.email || '—'}</p>
                  </div>
                </div>

                {/* Phone */}
                <div
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 hover:bg-white/60 group"
                  onClick={() => { if (candidate.phone) { navigator.clipboard.writeText(candidate.phone); toast.success('Phone copied'); } }}
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-50 to-emerald-100/80 rounded-lg flex items-center justify-center shrink-0 border border-emerald-200/50 shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all duration-200">
                    <Phone className="h-3.5 w-3.5 text-emerald-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.12em] mb-0.5">Contact</p>
                    <p className="text-[13px] font-semibold text-gray-800">{candidate.phone || '—'}</p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 hover:bg-white/60 group">
                  <div className="w-8 h-8 bg-gradient-to-br from-amber-50 to-orange-100/70 rounded-lg flex items-center justify-center shrink-0 border border-amber-200/50 shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all duration-200">
                    <MapPin className="h-3.5 w-3.5 text-amber-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.12em] mb-0.5">Location</p>
                    <p className="text-[13px] font-semibold text-gray-800 truncate">{candidate.location || '—'}</p>
                  </div>
                </div>

                {/* LinkedIn */}
                {candidate.linkedinUrl && (
                  <a href={candidate.linkedinUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 hover:bg-white/60 group">
                    <div className="w-8 h-8 bg-gradient-to-br from-indigo-50 to-indigo-100/80 rounded-lg flex items-center justify-center shrink-0 border border-indigo-200/50 shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all duration-200">
                      <Linkedin className="h-3.5 w-3.5 text-indigo-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.12em] mb-0.5">Social Network</p>
                      <div className="flex items-center gap-1.5">
                        <p className="text-[13px] font-semibold text-indigo-600 truncate group-hover:text-indigo-700 transition-colors">LinkedIn Profile</p>
                        <ExternalLink className="h-3 w-3 text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      </div>
                    </div>
                  </a>
                )}
              </div>
            </div>
            <div>
              {candidate.notes && (
                <div className="bg-white rounded-2xl border border-amber-200/50 overflow-hidden shadow-sm">
                  <div className="px-5 py-3.5 border-b border-amber-100/60 bg-gradient-to-r from-amber-50/80 via-orange-50/40 to-yellow-50/30">
                    <h3 className="text-[11px] font-extrabold text-amber-800 uppercase tracking-[0.15em] flex items-center gap-2">
                      <div className="w-6 h-6 bg-white/80 rounded-md flex items-center justify-center border border-amber-200/60 shadow-sm">
                        <FileText className="h-3.5 w-3.5 text-amber-600" />
                      </div>
                      Notes
                    </h3>
                  </div>
                  <div className="p-4">
                    <div className="text-[13px] text-gray-700 bg-amber-50/30 p-4 rounded-xl border border-amber-100/50 whitespace-pre-wrap break-words break-all leading-relaxed font-medium">
                      {candidate.notes}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Resume Card ── */}
            <div className="bg-indigo-50 rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="px-5 py-3.5 border-b border-gray-100/80 bg-gradient-to-r from-indigo-50/80 to-indigo-50/50">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-white rounded-md flex items-center justify-center border border-gray-200/60 shadow-sm">
                    <FileText className="h-3.5 w-3.5 text-gray-500" />
                  </div>
                  <span className="text-[11px] font-extrabold text-gray-600 uppercase tracking-[0.15em]">Candidate Resume</span>
                </div>
              </div>
              <div className="p-4">
                {candidate.resumeLink ? (
                  <div className="grid grid-cols-2 gap-2.5">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(getCompleteBackendUrl(candidate.resumeLink!), '_blank')}
                      className="h-10 text-xs font-bold uppercase tracking-wider rounded-xl border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                    >
                      Preview
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleDownloadResume}
                      className="h-10 text-xs font-bold uppercase tracking-wider rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 shadow-md shadow-indigo-200/50 transition-all duration-200"
                    >
                      Get PDF
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 bg-gray-50/30 rounded-xl border-2 border-dashed border-gray-200/70">
                    <FileText className="h-7 w-7 text-gray-300 mb-2.5" />
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">No Resume Uploaded</p>
                  </div>
                )}
              </div>
            </div>

            {/* ── Created By ── */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.12em]">Created By</span>
                <span className="text-[12px] font-bold text-gray-800 bg-gray-50 px-3 py-1 rounded-lg border border-gray-100">
                  {candidate.createdBy ? `${candidate.createdBy.firstName} ${candidate.createdBy.lastName || ''}`.trim() : 'System Recruiter'}
                </span>
              </div>
            </div>

            {/* ── Internal Assessment Notes ── */}

          </div>
        </div>


      </div>

      <AddCandidateDialog
        isEditMode={true}
        candidate={{
          ...candidate,
          currentSalary: normalizeSalary(candidate.currentSalary),
          expectedSalary: normalizeSalary(candidate.expectedSalary)
        }}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onUpdateCandidate={async (id, data) => {
          try {
            await candidateService.updateCandidate(id, data);
            fetchData();
            toast.success("Candidate updated successfully");
          } catch (err) {
            console.error(err);
          }
        }}
      />

      <AddInterviewDialog
        open={isAddInterviewDialogOpen}
        onOpenChange={(open) => {
          setIsAddInterviewDialogOpen(open);
          if (!open) setEditingInterview(null);
        }}
        onAddInterview={async () => {
          fetchData();
        }}
        onSuccess={() => {
          fetchData();
        }}
        interviewToEdit={editingInterview}
        isEditMode={!!editingInterview}
        initialCandidateId={candidate._id}
        existingInterviewsCount={interviews.length}
      />

      {/* Feedback Dialog */}
      <Dialog open={isFeedbackDialogOpen} onOpenChange={setIsFeedbackDialogOpen}>
        <DialogContent className="sm:max-w-[480px] border-none shadow-2xl p-0 overflow-hidden rounded-2xl bg-white">
          <div className="bg-indigo-50/50 p-6 border-b border-indigo-100/50">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 bg-white rounded-xl shadow-sm border border-indigo-100 flex items-center justify-center">
                <MessageSquare className="h-4.5 w-4.5 text-indigo-600" />
              </div>
              <DialogTitle className="text-lg font-bold text-gray-900 tracking-tight">Interview Assessment</DialogTitle>
            </div>
            <DialogDescription className="text-gray-500 text-xs font-medium leading-relaxed">
              Share your professional findings for <span className="text-indigo-600 font-bold">{candidateService.getCandidateFullName(candidate)}</span> to finalize this round.
            </DialogDescription>
          </div>

          <div className="p-6 space-y-5">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="feedback" className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.1em]">
                  Evaluation Notes
                </label>
                {!feedbackText.trim() && (
                  <span className="text-[9px] text-amber-500 font-bold bg-amber-50 px-2 py-0.5 rounded border border-amber-100/50 uppercase">Required</span>
                )}
              </div>
              <textarea
                id="feedback"
                className="w-full min-h-[160px] p-4 text-sm border border-gray-100 rounded-xl focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 transition-all outline-none resize-none bg-gray-50/50 placeholder:text-gray-300 font-medium"
                placeholder="Describe candidate strengths, technical gaps, and your final recommendation..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                variant="ghost"
                onClick={() => setIsFeedbackDialogOpen(false)}
                className="h-10 px-5 text-gray-400 hover:text-gray-600 text-xs font-bold uppercase tracking-wider"
              >
                Cancel
              </Button>
              <Button
                disabled={!feedbackText.trim()}
                onClick={() => pendingStatus && updateStatusConfirmed(pendingStatus, feedbackText)}
                className="h-10 px-6 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-wider rounded-xl shadow-lg shadow-indigo-200 transition-all"
              >
                Submit Feedback
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Confirmation Dialog ── */}
      <Dialog open={confirmDialog.open} onOpenChange={closeConfirmDialog}>
        <DialogContent className="sm:max-w-[440px] border-none shadow-2xl p-0 overflow-hidden rounded-2xl bg-white">
          <div className="p-7">
            <div className="flex gap-5">
              {/* ── Visual Indicator ── */}
              <div className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center shadow-lg transition-transform hover:scale-105 duration-300 ${confirmDialog.type === 'danger' ? 'bg-red-50 shadow-red-100/50' :
                confirmDialog.type === 'warning' ? 'bg-amber-50 shadow-amber-100/50' :
                  'bg-blue-50 shadow-blue-100/50'
                }`}>
                {confirmDialog.type === 'danger' && <AlertTriangle className="h-5 w-5 text-red-600" />}
                {confirmDialog.type === 'warning' && <AlertTriangle className="h-5 w-5 text-amber-600" />}
                {confirmDialog.type === 'info' && <Info className="h-5 w-5 text-blue-600" />}
              </div>

              {/* ── Content ── */}
              <div className="flex-1 min-w-0 pr-4">
                <h3 className="text-[17px] font-bold text-gray-900 tracking-tight leading-tight mb-2">
                  {confirmDialog.title}
                </h3>
                <p className="text-[13.5px] text-gray-500 leading-relaxed font-medium">
                  {confirmDialog.message}
                </p>
              </div>
            </div>

            {/* ── Professional Actions ── */}
            <div className="flex items-center justify-end gap-2.5 mt-8 pt-2">
              <Button
                variant="ghost"
                onClick={closeConfirmDialog}
                className="h-9 px-4 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 text-[11px] font-bold uppercase tracking-wider transition-all"
              >
                {confirmDialog.cancelText}
              </Button>
              <Button
                variant="outline"
                onClick={handleConfirmAction}
                className={`h-9 px-5 rounded-lg text-[11px] font-bold uppercase tracking-widest transition-all shadow-sm ${confirmDialog.type === 'danger' ? 'bg-red-500/10 text-red-600 border-red-200/50 hover:bg-red-500/20 hover:border-red-300 hover:text-red-700' :
                  confirmDialog.type === 'warning' ? 'bg-amber-500 hover:bg-amber-600 text-white border-none' :
                    'bg-indigo-600 hover:bg-indigo-700 text-white border-none'
                  }`}
              >
                {confirmDialog.confirmText}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
